import React from 'react';
import { Box, Button, IconButton, useTheme } from '@mui/material';
import { Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import type { RoleplayStatus } from '../types';

import feedbackIcon from '../../../assets/images/ic_report_1.svg';
import cancelIcon from '../../../assets/images/cancel_icon.svg';

export interface RoleplayControlButtonsProps {
  status: RoleplayStatus;
  hasFirstConversationCompleted: boolean;
  toggleRecording: () => void;
  handleEndSession: () => void;
  navigate: (path: string) => void;
  openMicDialog: () => void;
  speakButtonRef: React.RefObject<HTMLButtonElement | null>;
}

export const RoleplayControlButtons: React.FC<RoleplayControlButtonsProps> = ({
  status,
  hasFirstConversationCompleted,
  toggleRecording,
  handleEndSession,
  navigate,
  openMicDialog,
  speakButtonRef,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{
      position: 'absolute',
      top: '20px',
      right: '24px',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '202px',
    }}>
      <Button
        variant="contained"
        color={status === 'recording' ? 'error' : 'success'}
        onClick={toggleRecording}
        disabled={status === 'aiSpeaking' || status === 'processing' || status === 'waiting'}
        startIcon={status === 'recording' ? <StopIcon /> : <MicIcon />}
        ref={speakButtonRef as React.RefObject<HTMLButtonElement>}
        sx={{
          bgcolor: status === 'recording' ? theme.palette.error.main : '#5EB600',
          color: '#fff',
          borderRadius: '10px',
          padding: '8px 24px',
          marginBottom: '40px',
          fontSize: '14px',
          fontWeight: 400,
          height: '50px',
          animation: status === 'recording' ? 'pulse 1.5s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)' },
            '70%': { boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
          },
          '& .MuiButton-startIcon': { marginRight: '4px' },
        }}
      >
        {status === 'recording' ? '停止' : '発話する(SPACE)'}
      </Button>
      <Button
        variant="contained"
        onClick={handleEndSession}
        disabled={status === 'recording' || !hasFirstConversationCompleted}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          padding: '14px 24px',
          borderRadius: '10px',
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          height: '46px',
          color: '#fff',
          bgcolor: '#2769B1',
        }}
      >
        <img src={feedbackIcon} alt="フィードバックアイコン" style={{ width: '20px', height: '20px' }} />
        フィードバックへ
      </Button>
      <Button
        variant="contained"
        onClick={() => navigate('/scenarios')}
        disabled={status === 'recording'}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          padding: '14px 24px',
          borderRadius: '10px',
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          height: '46px',
          color: '#fff',
          bgcolor: '#2769B1',
        }}
      >
        <img src={cancelIcon} alt="キャンセルアイコン" style={{ width: '20px', height: '20px' }} />
        途中でやめて戻る
      </Button>
      <IconButton
        onClick={openMicDialog}
        disabled={status === 'recording'}
        sx={{
          bgcolor: '#f0f7ff',
          color: '#25507A',
          border: '1px solid #6C9AD7',
          borderRadius: '8px',
          padding: '8px',
          '&:hover': { bgcolor: '#e0f0ff' },
          alignSelf: 'flex-end',
          width: '40px',
          height: '40px',
        }}
        title="マイクを選択"
      >
        <MicIcon />
      </IconButton>
    </Box>
  );
};
